import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { useLang } from "../../i18n/LangContext";
import { useReveal } from "../../hooks/useReveal";
import { api } from "../../lib/api";
import { Section, VCOLOR, VERTICAL_KEYS } from "./Primitives";

export const Model = () => {
  const { lang } = useLang();
  const ref = useReveal();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/model").then((r) => setData(r.data)).catch(() => setData(false)); }, []);
  const circ = 2 * Math.PI * 78;
  if (!data) return <div className="wrap" data-testid="model-loading">{data === null && <Loader2 className="spin" aria-label="Loading" />}</div>;
  const m = data[lang];
  return (
    <Section id="modele-economique" eyebrow={m.eyebrow} title={m.title} testid="model-section">
      <div className="model-grid">
        <div className="panel spec" data-testid="model-spec-table">
          {m.rows.map(([k, v, d]) => (
            <div className="spec-row" key={k}>
              <div className="spec-k">{k}</div>
              <div className="spec-v"><b>{v}</b><span>{d}</span></div>
            </div>
          ))}
        </div>
        <div className="panel dial-card reveal" ref={ref} data-testid="model-dial">
          <span className="s-num">{m.dialLabel}</span>
          <div className="dial">
            <svg viewBox="0 0 180 180" aria-hidden="true">
              <circle cx="90" cy="90" r="78" fill="none" stroke="rgba(230,236,240,0.1)" strokeWidth="8" />
              <circle className="dial-arc" cx="90" cy="90" r="78" fill="none" stroke="var(--amber)" strokeWidth="8" strokeLinecap="round"
                strokeDasharray={circ} style={{ strokeDashoffset: circ * 0.8 }} />
            </svg>
            <div className="dial-num">20%</div>
          </div>
          <p>{m.dialDesc}</p>
        </div>
      </div>
    </Section>
  );
};

const NODES = [
  ["togo", 120, 130, "agro"], ["ci", 260, 75, "agro"], ["sn", 400, 55, "agro"], ["cm", 540, 75, "agro"], ["bj", 680, 130, "agro"],
  ["gn", 140, 250, "mines"], ["ml", 660, 250, "mines"], ["bf", 520, 345, "mines", 533, 365],
  ["ga", 220, 400, "petrole", 220, 422], ["cg", 400, 430, "petrole", 400, 452], ["sn", 580, 400, "petrole", 580, 422],
  ["ma", 90, 190, "numerique"], ["rw", 710, 190, "numerique"],
];

export const Network = () => {
  const { t } = useLang();
  return (
    <Section id="reseau" eyebrow={t.network.eyebrow} title={t.network.title} desc={t.network.desc} testid="network-section">
      <div className="panel hub-wrap">
        <svg className="hub-svg" viewBox="0 0 800 480" width="100%" role="img" aria-label={t.network.aria} data-testid="network-map">
          {NODES.map(([, x, y, v], i) => (
            <g key={`l${i}`}>
              <line className="hub-line" x1="400" y1="250" x2={x} y2={y} />
              <line x1="400" y1="250" x2={x} y2={y} stroke={VCOLOR[v]} strokeOpacity="0.55" strokeWidth="1.2" className="hub-pulse" style={{ animationDelay: `${i * 0.18}s` }} />
            </g>
          ))}
          <circle cx="400" cy="250" r="44" fill="none" stroke="rgba(232,169,58,0.18)" strokeWidth="1" />
          <circle cx="400" cy="250" r="34" fill="#1D2731" stroke="var(--amber)" strokeWidth="1.6" />
          <text x="400" y="246" textAnchor="middle" fill="#fff" fontSize="11">HUB</text>
          <text x="400" y="261" textAnchor="middle" fill="#B4BDC7" fontSize="9">FR / EU</text>
          <g fontSize="12" fill="#EDEFF1" textAnchor="middle">
            {NODES.map(([k, x, y, v, tx, ty], i) => (
              <g key={`n${i}`}>
                <circle cx={x} cy={y} r="10" fill={VCOLOR[v]} opacity="0.15" />
                <circle cx={x} cy={y} r="6" fill={VCOLOR[v]} />
                <text x={tx ?? x} y={ty ?? y - 15}>{t.network.countries[k]}</text>
              </g>
            ))}
          </g>
        </svg>
        <div className="legend">
          {VERTICAL_KEYS.map((k) => <span key={k}><i style={{ background: VCOLOR[k] }} />{t.verticals[k]}</span>)}
        </div>
      </div>
    </Section>
  );
};
